import { Activity, Cpu, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MonitoringScreenProps {
  className?: string;
}

type ServiceStatus = 'healthy' | 'degraded';

interface Service {
  nameKey: string;
  descriptionKey: string;
  status: ServiceStatus;
  responseTime: string;
}

const SERVICES: readonly Service[] = [
  { nameKey: 'service.api.name', descriptionKey: 'service.api.description', status: 'healthy', responseTime: '12 ms' },
  { nameKey: 'service.qdrant.name', descriptionKey: 'service.qdrant.description', status: 'healthy', responseTime: '8 ms' },
  { nameKey: 'service.valkey.name', descriptionKey: 'service.valkey.description', status: 'healthy', responseTime: '3 ms' },
  { nameKey: 'service.postgres.name', descriptionKey: 'service.postgres.description', status: 'healthy', responseTime: '5 ms' },
  { nameKey: 'service.ollama.name', descriptionKey: 'service.ollama.description', status: 'healthy', responseTime: '240 ms' },
  { nameKey: 'service.embedding.name', descriptionKey: 'service.embedding.description', status: 'healthy', responseTime: '31 ms' },
  { nameKey: 'service.comfyui.name', descriptionKey: 'service.comfyui.description', status: 'degraded', responseTime: '1,2 s' },
  { nameKey: 'service.piper.name', descriptionKey: 'service.piper.description', status: 'healthy', responseTime: '44 ms' },
];

// Fake but plausible "Anfragen/min" sparkline over the last 12 minutes.
const REQUESTS_PER_MIN = [38, 44, 41, 52, 60, 57, 71, 66, 74, 83, 79, 88];

const C = {
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  textPrimary: 'var(--bnkp-text, #E2E8F0)',
  textSecondary: 'var(--bnkp-text2, #94A3C2)',
  textTertiary: 'var(--bnkp-text3, #4A5568)',
  blue: 'var(--bnkp-primary, #2D7EFF)',
  green: 'var(--bnkp-green, #34D399)',
  amber: 'var(--bnkp-amber, #FBBF24)',
} as const;

export function MonitoringScreen({ className }: MonitoringScreenProps) {
  const { t } = useTranslation('demo');
  const maxReq = Math.max(...REQUESTS_PER_MIN);

  return (
    <section
      className={className}
      style={{ fontFamily: 'Inter, sans-serif', color: C.textPrimary, maxWidth: 900 }}
    >
      <style>{`
        @keyframes mon-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.45); }
          50% { box-shadow: 0 0 0 5px rgba(52,211,153,0); }
        }
        @keyframes mon-amber-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.45); }
          50% { box-shadow: 0 0 0 5px rgba(251,191,36,0); }
        }
        .mon-dot-healthy { animation: mon-pulse 2s ease-in-out infinite; }
        .mon-dot-degraded { animation: mon-amber-pulse 1.4s ease-in-out infinite; }
        .mon-card { transition: border-color .18s ease, background .18s ease; }
        .mon-card:hover { background: rgba(255,255,255,0.04); border-color: ${C.borderStrong}; }
        /* On phones keep the service list focused (the count still implies the
           full fleet) so the screen isn't an endless scroll. */
        @media (max-width: 640px) { .mon-card:nth-child(n+5) { display: none; } }
      `}</style>

      {/* Header */}
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 12,
            background: 'rgba(45,126,255,0.10)',
          }}
        >
          <Activity size={20} color={C.blue} />
        </span>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: -0.2 }}>
            {t('monitoring.title')}
          </h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: C.textSecondary }}>
            {t('monitoring.subtitle')}
          </p>
        </div>
        <button
          type="button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 600,
            color: C.textSecondary,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          {t('monitoring.refresh')}
        </button>
      </header>

      {/* GPU card */}
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Cpu size={16} color={C.blue} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>GPU</span>
          <span style={{ fontSize: 13, color: C.textSecondary }}>· NVIDIA RTX 4090</span>
        </div>

        <div style={{ height: 8, borderRadius: 999, background: C.elevated, overflow: 'hidden' }}>
          <div
            style={{
              width: '47%',
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${C.blue}, #00D4FF)`,
            }}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginTop: 18,
          }}
        >
          {[
            { label: t('monitoring.gpu.utilization'), value: '47 %' },
            { label: t('monitoring.gpu.memory'), value: '17,8 / 24 GB' },
            { label: t('monitoring.gpu.temperature'), value: '62 °C' },
          ].map((stat) => (
            <div key={stat.label}>
              <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 4 }}>
                {stat.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Requests per minute sparkline */}
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 20,
          marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{t('monitoring.requestsPerMin')}</span>
          <span style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>
            {t('monitoring.requestsCurrent', { count: 88 })}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 56 }}>
          {REQUESTS_PER_MIN.map((value, i) => (
            <div
              key={i}
              title={t('monitoring.requestsTooltip', { value })}
              style={{
                flex: 1,
                height: `${Math.round((value / maxReq) * 100)}%`,
                borderRadius: 4,
                background: i === REQUESTS_PER_MIN.length - 1 ? C.blue : 'rgba(45,126,255,0.35)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Service health grid */}
      <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: C.textSecondary }}>
        {t('monitoring.servicesHeading')}
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 14,
        }}
      >
        {SERVICES.map((svc) => {
          const isHealthy = svc.status === 'healthy';
          const dotColor = isHealthy ? C.green : C.amber;
          return (
            <div
              key={svc.nameKey}
              className="mon-card"
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span
                  className={isHealthy ? 'mon-dot-healthy' : 'mon-dot-degraded'}
                  style={{
                    flexShrink: 0,
                    width: 10,
                    height: 10,
                    marginTop: 5,
                    borderRadius: 999,
                    background: dotColor,
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t(`monitoring.${svc.nameKey}`)}</div>
                  <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>
                    {t(`monitoring.${svc.descriptionKey}`)}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 600, color: dotColor }}>
                  {isHealthy ? t('monitoring.status.healthy') : t('monitoring.status.degraded')}
                </span>
                <span style={{ fontSize: 12, color: C.textTertiary }}>{svc.responseTime}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
